import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { EASE, LogoMark } from "./Reveal";

const LINKS = [
  { label: "Experiences", href: "/experiences" },
  { label: "Groups", href: "/group-packages" },
  { label: "The Island", href: "/the-island" },
  { label: "The Lodge", href: "/the-lodge" },
  { label: "Gift Vouchers", href: "/vouchers" },
  { label: "FAQ", href: "/#faq" },
  { label: "Contact", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const isActive = (href) =>
    href.startsWith("/#") ? false : pathname === href || (href !== "/" && pathname.startsWith(href));

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-white/5 bg-navy-800/90 backdrop-blur-xl"
            : "border-b border-white/5 bg-navy-800/40 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img src="/images/logo-dark.png" alt="The Adventure Islands" className="h-9 w-auto sm:h-10" />
          </Link>

          <nav className="hidden items-center gap-5 lg:flex xl:gap-7">
            {LINKS.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[11px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 hover:text-gold ${
                  isActive(l.href) ? "text-gold" : "text-ivory/80"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href="tel:+353862518252"
              data-testid="nav-phone"
              className="hidden items-center gap-2 text-[13px] tracking-wide text-gold transition-colors hover:text-gold-light xl:flex"
            >
              <Phone className="h-3.5 w-3.5" />
              +353 86 251 8252
            </a>
            <Link
              to="/contact"
              data-testid="nav-enquire-button"
              className="hidden bg-gold px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-navy-800 transition-colors duration-300 hover:bg-gold-light sm:inline-flex"
            >
              Enquire Now
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-menu-open"
              aria-label="Open menu"
              className="border border-white/15 p-2.5 text-ivory transition-colors hover:border-gold hover:text-gold lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex flex-col bg-navy-900/98 backdrop-blur-xl"
          >
            <div className="flex h-20 items-center justify-between px-5 sm:px-8">
              <div className="flex items-center gap-3">
                <img src="/images/logo-dark.png" alt="The Adventure Islands" className="h-10 w-auto" />
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                aria-label="Close menu"
                className="border border-white/15 p-2.5 text-ivory transition-colors hover:border-gold hover:text-gold"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.href}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.08 + i * 0.06, ease: EASE }}
                >
                  <Link
                    to={l.href}
                    onClick={() => setOpen(false)}
                    className="block border-b border-white/5 py-4 font-serif text-4xl text-ivory transition-colors hover:text-gold"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="flex flex-col gap-4 px-8 pb-10">
              <a
                href="tel:+353862518252"
                className="flex items-center gap-2 text-sm tracking-wide text-gold"
              >
                <Phone className="h-4 w-4" /> +353 86 251 8252
              </a>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="bg-gold px-6 py-4 text-center text-[12px] font-semibold uppercase tracking-[0.22em] text-navy-800"
              >
                Enquire Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
